import React, { useState, useEffect, useRef } from 'react';

// --- NEW: Importing local images for Before/After Interior Design ---
import BeforeInterior from '../../../assets/Images/BeforeInterior.png';
import AfterInterior from '../../../assets/Images/AfterInterior.png';

// --- AnimateOnScroll Component (no changes needed here) ---
const AnimateOnScroll = ({ children, direction = 'up', delay = 0 }) => {
    const [isVisible, setIsVisible] = useState(false);
    const ref = useRef(null);

    const directionClasses = {
        up: 'translate-y-20', down: '-translate-y-20',
        left: '-translate-x-20', right: 'translate-x-20',
    };

    const initialClasses = `opacity-0 ${directionClasses[direction]}`;
    const finalClasses = 'opacity-100 translate-y-0 translate-x-0';

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            setIsVisible(entry.isIntersecting);
        }, { threshold: 0.1 });

        const currentRef = ref.current;
        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, []);

    return (
        <div 
            ref={ref}
            className={`transition-all duration-1500`} // Using smoother duration/timing
            style={{ 
                transform: isVisible ? finalClasses : initialClasses,
                transitionDelay: `${delay}ms`,
                transitionTimingFunction: 'cubic-bezier(0.23, 1, 0.32, 1)' 
            }}
        >
            {children}
        </div>
    );
};


const BeforeAfter = () => {
    const [sliderValue, setSliderValue] = useState(50);
    const sliderContainerRef = useRef(null);

    const handleSliderChange = (e) => {
        setSliderValue(e.target.value);
    };

    // --- NEW: This handler will update the slider based on mouse position ---
    const handleMouseMove = (e) => {
        if (!sliderContainerRef.current) return;

        const rect = sliderContainerRef.current.getBoundingClientRect();
        
        // Calculate the mouse's horizontal position relative to the container (from 0 to the container's width)
        const x = e.clientX - rect.left;

        // Convert the pixel value to a percentage (from 0 to 100)
        const percentage = (x / rect.width) * 100;

        // Clamp the value between 0 and 100 to avoid glitches at the edges
        const clampedPercentage = Math.max(0, Math.min(100, percentage));

        // Update the state
        setSliderValue(clampedPercentage);
    };

    return (
        <section id="before-after" className="py-24 bg-gray-50"> 
            <style>{`
                /* Custom styles for Red/Black/White theme */
                .text-gradient-red-black {
                    background-image: linear-gradient(to right, #DC2626, #1F2937); /* Red and Dark Gray/Black */
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }
                .ba-slider {
                    position: relative;
                    overflow: hidden;
                    cursor: grab;
                }
                .ba-slider * {
                    user-select: none;
                }
                .ba-slider img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
                .ba-slider .resize {
                    position: absolute;
                    top: 0;
                    left: 0;
                    height: 100%;
                    overflow: hidden;
                    /* Use the background image (Before) for the sliding part */
                }
                .ba-slider .resize img {
                    /* This image needs to be the same size and offset as the background image */
                    position: absolute;
                    left: 0;
                }
                /* The visible separator line */
                .ba-slider .handle {
                    position: absolute;
                    top: 0;
                    width: 4px; /* Thickness of the separator */
                    height: 100%;
                    background: #DC2626; /* Red Separator */
                    transform: translateX(-50%);
                    z-index: 10;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                /* The central icon/grabber on the separator */
                .ba-slider .handle::before {
                    content: '|||'; 
                    color: white; /* White grabber icon */
                    font-size: 1.25rem;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    background: #1F2937; /* Dark Grabber Background */
                    border: 4px solid #DC2626; /* Red border accent */
                    cursor: pointer;
                    line-height: 1;
                }
                /* Hide the default range input slider track */
                .ba-slider input[type=range] {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    margin: 0;
                    padding: 0;
                    opacity: 0; /* Hide the default track/thumb */
                    cursor: grab;
                    z-index: 20; /* Keep it on top for interaction */
                }
            `}</style>
            
            <div className="container mx-auto px-6 text-center">
                <AnimateOnScroll direction="up">
                    <h2 className="text-4xl md:text-5xl headline-font text-gradient-red-black">Experience the Design Transformation</h2>
                    <p className="text-xl mt-4 max-w-2xl mx-auto text-gray-700">See the dramatic difference meticulous planning and flawless execution make. Drag the slider to unveil the power of custom interior design.</p>
                </AnimateOnScroll>
                
                <AnimateOnScroll direction="up" delay={200}>
                    <div 
                        ref={sliderContainerRef}
                        onMouseMove={handleMouseMove}
                        className="ba-slider max-w-4xl w-full h-[60vh] mx-auto shadow-2xl mt-12"
                        // Add onMouseLeave to reset cursor/stop calculation if needed, though not strictly required for this logic
                        // onMouseLeave={() => {/* optional: reset hover state or mouse tracking */}} 
                    >
                        {/* Image 1: The 'After' (Background Image) */}
                        <img src={AfterInterior} alt="Transformed Interior Design" className="background-img" />
                        
                        {/* The sliding layer (Image 2: The 'Before') */}
                        <div className="resize" style={{ width: `${sliderValue}%` }}>
                            <img src={BeforeInterior} alt="Original Interior State" />
                        </div>
                        
                        {/* Separator Handle */}
                        <div className="handle" style={{ left: `${sliderValue}%` }}></div>
                        
                        {/* Range input for accessibility and touch interaction */}
                        <input 
                            type="range" 
                            min="0" 
                            max="100" 
                            value={sliderValue} 
                            className="slider" 
                            onChange={handleSliderChange} 
                        />
                    </div>
                </AnimateOnScroll>
                
                {/* --- Visual Labels (Optional but recommended for clarity) --- */}
                <div className="flex justify-between max-w-4xl mx-auto mt-4 text-lg font-semibold">
                    <span className="text-gray-900">BEFORE (Original State)</span>
                    <span className="text-red-600">AFTER (Final Design)</span>
                </div>
            </div>
        </section>
    );
};

export default BeforeAfter;